import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

interface LeadChatContextValue {
  isOpen: boolean;
  /** True once the visitor opened the chat at least once this page load (mounts the lazy widget). */
  activated: boolean;
  unread: number;
  open: () => void;
  close: () => void;
  setUnread: (n: number) => void;
}

const LeadChatContext = createContext<LeadChatContextValue>({
  isOpen: false,
  activated: false,
  unread: 0,
  open: () => undefined,
  close: () => undefined,
  setUnread: () => undefined
});

/**
 * Open/close state for the website chat widget, so any CTA on the page
 * (Header, FinalCta, the ?chat=1 deep link) can open it — same shape as
 * DemoDrawerContext.
 */
export const LeadChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activated, setActivated] = useState(false);
  const [unread, setUnread] = useState(0);

  const open = useCallback(() => {
    setActivated(true);
    setIsOpen(true);
    setUnread(0);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(
    () => ({ isOpen, activated, unread, open, close, setUnread }),
    [isOpen, activated, unread, open, close]
  );
  return <LeadChatContext.Provider value={value}>{children}</LeadChatContext.Provider>;
};

export const useLeadChat = (): LeadChatContextValue => useContext(LeadChatContext);
